import * as Sentry from '@sentry/react';

export const RBAC_DENIAL_PREFIX = 'Missing permission: ';

type Listener = () => void;

let deniedCodes: string[] = [];
const listeners = new Set<Listener>();

export const getRbacDenials = (): string[] => deniedCodes;

export const subscribeToRbacDenials = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const resetRbacDenials = (): void => {
  deniedCodes = [];
  listeners.forEach((listener) => listener());
};

export const recordRbacDenial = (code: string): void => {
  if (deniedCodes.includes(code)) return;
  deniedCodes = [...deniedCodes, code];
  Sentry.captureMessage(`rbac denial: ${code}`, 'warning');
  listeners.forEach((listener) => listener());
};

const inspectForbiddenResponse = (response: Response): void => {
  // Clone so the actual caller can still consume the body.
  response.clone().json()
    .then((data: unknown) => {
      const detail = (data as { detail?: unknown } | null)?.detail;
      if (typeof detail === 'string' && detail.startsWith(RBAC_DENIAL_PREFIX)) {
        recordRbacDenial(detail.slice(RBAC_DENIAL_PREFIX.length));
      }
    })
    .catch(() => undefined);
};

let installed = false;

/**
 * Wraps `window.fetch` to observe RBAC 403s (`{"detail": "Missing permission:
 * <code>"}`) from srv-ergeon. The fetch layer is patched instead of
 * `makeRequest` because requests also originate from SDKs (`@ergeon/file-storage`,
 * erg-auth) and raw `fetch` calls, and because most callers swallow errors before
 * they reach any UI. Temporary surface for the RBAC rollout — proper per-page
 * handling and proactive permission gating replace it later.
 */
export const installRbacDenialWatcher = (): void => {
  if (installed) return;
  installed = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = async(input, init) => {
    const response = await originalFetch(input, init);
    if (response.status === 403) inspectForbiddenResponse(response);
    return response;
  };
};
